import { Avatar } from '@/kit/Avatar';
import { statusToneClass } from '@/core/statusTone';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import type { Dayjs } from 'dayjs';
import { NotificationTextMessage } from '@/core/enums/notification';
import { notifyError, notifySuccess } from '@/core/notify/notify';
import { dateRangeProblem } from '@/core/utils/dates';
import { ExportFormatCode, toIsoDate, type ClientGridQuery, type ClientGridRow } from '@/core/models/practice.models';
import type { DashboardTileRow } from '@/core/models/workspace.models';
import { selectClient } from '@/core/selectedClient';
import { practiceService } from '@/core/services/practice';
import { workspaceService } from '@/core/services/workspace';
import { Button } from '@/kit/Button';
import { Checkbox } from '@/kit/Checkbox';
import { ClosePopup } from '@/kit/ClosePopup';
import { DataTable, type DataTableColumn, type DataTableSort } from '@/kit/DataTable';
import { DateField } from '@/kit/DateField';
import { openDialog } from '@/kit/dialog';
import { Icon } from '@/kit/Icon';
import { Paginator } from '@/kit/Paginator';
import { Select } from '@/kit/Select';
import { ExportMenu } from './ExportMenu';
import { SearchBox } from './SearchBox';
import { useDebouncedSearch } from './useDebouncedSearch';
import { parseAccountsFilters, type AccountsListTile, type AccountsUrlFilters } from './accountsFilters';
import './AccountsClient.scss';

/** ap5-a4: the due-date filter values the grid offers; 8 is the API's custom range. */
const ALL = -1;
const CUSTOM = 8;
/** Not a server option: sent as a custom range that ends yesterday / starts today. */
const OVERDUE = 101;
const NOT_YET_DUE = 102;

/** BUG-055: the 3.0 page sizes. */
const PAGE_SIZE_OPTIONS = [15, 25, 50, 100];

/** ap5-a4: every option the API's DueRange understands, plus overdue / not yet due. */
const dueOptions = [
  { id: ALL, name: 'All due dates' },
  { id: OVERDUE, name: 'Overdue' },
  { id: NOT_YET_DUE, name: 'Not yet due' },
  { id: 0, name: 'Due in 30 Days' },
  // BUG-055: the 3.0 "due within" choices beyond 30 days (DueRange 9-12).
  { id: 9, name: 'Due in 60 Days' },
  { id: 10, name: 'Due in 120 Days' },
  { id: 11, name: 'Due in 180 Days' },
  { id: 12, name: 'Due in 360 Days' },
  { id: 1, name: 'Last Week' },
  { id: 2, name: 'This Month' },
  { id: 3, name: 'Last Month' },
  { id: 4, name: 'This Quarter' },
  { id: 5, name: 'Last Quarter' },
  { id: 6, name: 'This Year' },
  { id: 7, name: 'Last Year' },
  { id: CUSTOM, name: 'Custom' },
];

/** ap5-a4: the accounting-period statuses the API filters on (PeriodDefaults.StatusLabel). */
const statusOptions = [
  { id: ALL, name: 'Accounts Status' },
  { id: 0, name: 'Not Started' },
  { id: 1, name: 'In Progress' },
  { id: 2, name: 'Ready for Review' },
  { id: 3, name: 'Under Review' },
  { id: 4, name: 'Approved' },
  { id: 5, name: 'Finalised' },
  { id: 6, name: 'Submitted' },
];

/** ap5-a4: every client type the API accepts (ClientTypes). */
const clientTypeOptions = [
  { id: ALL, name: 'Client Type' },
  { id: 0, name: 'Limited Company' },
  { id: 1, name: 'Sole Trader' },
  { id: 2, name: 'Partnership' },
  { id: 3, name: 'LLP' },
];

const initialQuery: ClientGridQuery = {
  pageNumber: 1,
  pageSize: 25,
  // The newest client is listed first; a column header re-sorts the whole list on the server.
  sortBy: 'added',
  sortOrder: false,
  searchText: '',
  statusType: -1,
  clientType: null,
  dueFilter: null,
  startDate: null,
  endDate: null,
};

function offsetToday(days: number): string {
  const day = new Date();
  day.setDate(day.getDate() + days);
  return toIsoDate(day)!;
}

/** The clients a dashboard list names (out of balance, locked), shaped like grid rows; one row per client. */
function tileRowsToGridRows(rows: DashboardTileRow[]): ClientGridRow[] {
  const seen = new Set<string>();
  const result: ClientGridRow[] = [];
  for (const row of rows) {
    if (seen.has(row.clientId)) continue;
    seen.add(row.clientId);
    result.push({
      universalId: row.clientId,
      clientId: row.clientCode,
      clientName: row.clientName,
      clientType: '-',
      clientTypeId: -1,
      period: row.period,
      dueDate: row.dueDate ?? '-',
      status: row.status,
      statusId: null,
      accountingPeriodId: null,
      registrationNo: null,
      utrNo: null,
      isActive: true,
      hasAccountingData: true,
    });
  }
  return result;
}

/** The "Accounts" landing grid: pick a client here to work on it in the /home/taxreturns/* screens. */
export function AccountsClient() {
  const [dataSource, setDataSource] = useState<ClientGridRow[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [dueFilter, setDueFilter] = useState<number>(ALL);
  const [statusFilter, setStatusFilter] = useState<number>(ALL);
  const [clientTypeFilter, setClientTypeFilter] = useState<number>(ALL);
  const [customFrom, setCustomFrom] = useState<Dayjs | null>(null);
  const [customTo, setCustomTo] = useState<Dayjs | null>(null);

  // prod-p2: the list is paged on the server; only one page of clients is ever fetched.
  const [totalRecord, setTotalRecord] = useState(0);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  // The private Angular fields, kept in refs so every handler works on the latest value.
  const query = useRef<ClientGridQuery>(initialQuery);
  const pageSizeRef = useRef(25);
  const [sort, setSort] = useState<DataTableSort>({ key: 'added', ascending: false });

  // APT377: a filter given in the URL by the dashboard, shown as "Filtered by: X  [Clear]".
  const [searchParams, setSearchParams] = useSearchParams();
  const paramsKey = searchParams.toString();
  const appliedKey = useRef<string | null>(null);
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  /** Set while the list shows the clients of a dashboard list (out of balance, locked) instead of the grid query. */
  const tileClients = useRef<ClientGridRow[] | null>(null);

  const getList = useCallback(() => {
    practiceService.getClientGrid(query.current).then(
      (response) => {
        setDataSource(response.clientList);
        setTotalRecord(response.pagination.totalItemCount);
        setSelectedIds(new Set());
      },
      (error) => notifyError(error),
    );
  }, []);

  /** prod-p2: any filter change goes back to the first page. */
  const applyFilter = useCallback(
    (patch: Partial<ClientGridQuery>) => {
      setPageIndex(0);
      query.current = { ...query.current, ...patch, pageNumber: 1, pageSize: pageSizeRef.current };
      getList();
    },
    [getList],
  );

  /** Shows one page of the clients a dashboard list named; these lists have no server paging. */
  const showTilePage = useCallback((rows: ClientGridRow[], index: number, size: number) => {
    setDataSource(rows.slice(index * size, index * size + size));
    setTotalRecord(rows.length);
    setSelectedIds(new Set());
  }, []);

  const loadTileClients = useCallback(
    (tile: AccountsListTile) => {
      tileClients.current = [];
      setDataSource([]);
      setTotalRecord(0);
      workspaceService.getDashboardTileList({ tile, pageNumber: 1, pageSize: 500 }).then(
        (list) => {
          if (tileClients.current === null) return;
          const rows = tileRowsToGridRows(list.rows);
          tileClients.current = rows;
          showTilePage(rows, 0, pageSizeRef.current);
        },
        (error) => notifyError(error),
      );
    },
    [showTilePage],
  );

  /** Turns the chosen option into the API's dueFilter plus, for a range, its two dates. */
  const dueQuery = (due: number, from: Dayjs | null, to: Dayjs | null): Partial<ClientGridQuery> => {
    let dueFilterValue: number | null = null;
    let startDate: string | null = null;
    let endDate: string | null = null;

    if (due === OVERDUE) {
      dueFilterValue = CUSTOM;
      endDate = offsetToday(-1);
    } else if (due === NOT_YET_DUE) {
      dueFilterValue = CUSTOM;
      startDate = offsetToday(0);
    } else if (due === CUSTOM) {
      dueFilterValue = CUSTOM;
      startDate = toIsoDate(from);
      endDate = toIsoDate(to);
    } else if (due >= 0) {
      dueFilterValue = due;
    }

    return { dueFilter: dueFilterValue, startDate, endDate };
  };

  /** Puts the list into the state a URL filter asks for (the due-date and status filters are replaced). */
  const applyUrlFilters = (filters: AccountsUrlFilters) => {
    setActiveFilters(filters.labels);
    setCustomFrom(null);
    setCustomTo(null);
    const due =
      filters.due === 'overdue'
        ? OVERDUE
        : filters.due === 'notYetDue'
          ? NOT_YET_DUE
          : filters.due === 'in30'
            ? 0
            : ALL;
    const status = filters.statusId ?? ALL;
    setDueFilter(due);
    setStatusFilter(status);
    setPageIndex(0);
    if (filters.tile) {
      setDueFilter(ALL);
      setStatusFilter(ALL);
      query.current = { ...query.current, statusType: -1, dueFilter: null, startDate: null, endDate: null };
      loadTileClients(filters.tile);
      return;
    }
    tileClients.current = null;
    applyFilter({ statusType: status, ...dueQuery(due, null, null) });
  };

  // The first run loads the grid as it always did; a later change of the URL filter (a header link, Clear) re-applies it.
  useEffect(() => {
    if (appliedKey.current === paramsKey) return;
    const first = appliedKey.current === null;
    appliedKey.current = paramsKey;
    const filters = parseAccountsFilters(new URLSearchParams(paramsKey));
    if (first && filters.labels.length === 0) {
      getList();
      return;
    }
    applyUrlFilters(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  /** A filter the user changes by hand replaces the dashboard's: the chip goes and the URL is cleaned up. */
  const leaveUrlFilter = (onlyDashboardList = false) => {
    if (onlyDashboardList && tileClients.current === null) return;
    if (activeFilters.length === 0 && tileClients.current === null) return;
    tileClients.current = null;
    setActiveFilters([]);
    appliedKey.current = '';
    setSearchParams({}, { replace: true });
  };

  const clearUrlFilter = () => setSearchParams({}, { replace: true });

  /** prod-p2: moves the server-side page; the filters and the sort order are kept. */
  const onPage = (event: { pageIndex: number; pageSize: number }) => {
    setPageIndex(event.pageIndex);
    setPageSize(event.pageSize);
    pageSizeRef.current = event.pageSize;
    if (tileClients.current) {
      showTilePage(tileClients.current, event.pageIndex, event.pageSize);
      return;
    }
    query.current = { ...query.current, pageNumber: event.pageIndex + 1, pageSize: event.pageSize };
    getList();
  };

  /** Header click: the same column flips the direction, a new column starts ascending; back to page 1. */
  const onSort = (key: string) => {
    if (tileClients.current) return;
    const ascending = sort.key === key ? !sort.ascending : true;
    setSort({ key, ascending });
    setPageIndex(0);
    query.current = { ...query.current, sortBy: key, sortOrder: ascending, pageNumber: 1 };
    getList();
  };

  const onSearch = useDebouncedSearch((searchText) => {
    leaveUrlFilter(true);
    applyFilter({ searchText });
  });

  const applyDue = (due: number, from: Dayjs | null, to: Dayjs | null) => applyFilter(dueQuery(due, from, to));

  /** ap5-a4: Custom applies immediately, even before both dates are picked (the Angular comment says otherwise). */
  const onDueChange = (value: number | null) => {
    leaveUrlFilter();
    const due = value ?? ALL;
    setDueFilter(due);
    let from = customFrom;
    let to = customTo;
    if (due !== CUSTOM) {
      from = null;
      to = null;
      setCustomFrom(null);
      setCustomTo(null);
    }
    applyDue(due, from, to);
  };

  const onCustomDateChange = (from: Dayjs | null, to: Dayjs | null) => {
    if (dueFilter === CUSTOM) {
      // A range that ends before it starts is refused here instead of being searched.
      const problem = dateRangeProblem(from, to, 'The due date range');
      if (problem) {
        notifyError(problem);
        return;
      }
      applyDue(dueFilter, from, to);
    }
  };

  const onStatusChange = (value: number | null) => {
    leaveUrlFilter();
    const status = value ?? ALL;
    setStatusFilter(status);
    applyFilter({ statusType: status });
  };

  const onClientTypeChange = (value: number | null) => {
    leaveUrlFilter(true);
    const clientType = value ?? ALL;
    setClientTypeFilter(clientType);
    applyFilter({ clientType: clientType < 0 ? null : clientType });
  };

  const onExport = (format: ExportFormatCode) => {
    practiceService
      .exportClients({
        format,
        // A dashboard list has no server-side filter, so what is shown is exported by its ids.
        ids:
          selectedIds.size === 0 && tileClients.current
            ? tileClients.current.map((client) => client.universalId)
            : [...selectedIds],
        searchText: query.current.searchText,
        statusType: query.current.statusType ?? -1,
        clientType: query.current.clientType,
        dueFilter: query.current.dueFilter,
        startDate: query.current.startDate,
        endDate: query.current.endDate,
      })
      .catch((error) => notifyError(error));
  };

  const isAllSelected = dataSource.length > 0 && dataSource.every((row) => selectedIds.has(row.universalId));

  const toggleAll = (checked: boolean) => {
    setSelectedIds(new Set(checked ? dataSource.map((row) => row.universalId) : []));
  };

  const toggleRow = (row: ClientGridRow, checked: boolean) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (checked) {
        next.add(row.universalId);
      } else {
        next.delete(row.universalId);
      }
      return next;
    });
  };

  /** As on Manage > Clients: the API refuses the whole batch when any client holds trial balances or journals. */
  const deleteSelected = () => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    const blocked = dataSource
      .filter((row) => selectedIds.has(row.universalId) && row.hasAccountingData)
      .map((row) => row.clientName);
    if (blocked.length > 0) {
      notifyError(
        `${blocked.join(', ')} ${blocked.length > 1 ? 'hold' : 'holds'} accounting data and cannot be deleted. Remove the trial balances and journals first.`,
      );
      return;
    }

    void openDialog(ClosePopup, { data: { itemName: ids.length > 1 ? 'Clients' : 'Client', count: ids.length } })
      .afterClosed()
      .then((confirmed) => {
        if (!confirmed) return;
        practiceService.deleteClients(ids).then(
          () => {
            notifySuccess(NotificationTextMessage.recordDeletedSuccessfully);
            getList();
          },
          (error) => notifyError(error),
        );
      });
  };

  const clientLink = (row: ClientGridRow, text: string) => (
    <Link
      className="link"
      to="/home/taxreturns/dashboard"
      title="Open this client"
      onClick={() => selectClient(row.universalId, row.clientName)}
    >
      {text}
    </Link>
  );

  const clientNameCell = (row: ClientGridRow) => (
    <span className="name-cell">
      <Avatar name={row.clientName} />
      {clientLink(row, row.clientName)}
    </span>
  );

  const columns: DataTableColumn<ClientGridRow>[] = [
    {
      id: 'id',
      header: (
        <>
          <Checkbox checked={isAllSelected} onChange={(event) => toggleAll(event.target.checked)} />
          <button
            type="button"
            className="table-sort-button"
            aria-label="Sort by ID"
            onClick={() => onSort('clientId')}
          >
            ID
            <span
              className="table-sort-arrow"
              data-dir={sort.key === 'clientId' ? (sort.ascending ? 'asc' : 'desc') : 'none'}
              aria-hidden="true"
            />
          </button>
        </>
      ),
      cell: (row) => (
        <>
          <Checkbox
            checked={selectedIds.has(row.universalId)}
            onChange={(event) => toggleRow(row, event.target.checked)}
          />
          {clientLink(row, row.clientId)}
        </>
      ),
    },
    { id: 'clientname', sortKey: 'clientName', header: 'Client Name', cell: clientNameCell },
    { id: 'clienttype', sortKey: 'clientType', header: 'Client Type', cell: (row) => row.clientType },
    { id: 'period', header: 'Accounting Period', cell: (row) => row.period },
    { id: 'date', sortKey: 'duedate', header: 'Due Date', cell: (row) => row.dueDate },
    {
      id: 'status',
      header: 'Accounts Status',
      cell: (row) => <div className={statusToneClass(row.status) || 'text-green'}>{row.status}</div>,
    },
  ];

  return (
    <div className="content client-list accounts-client-page">
      <div className="content-body">
        <div className="content-header mb-0">
          <div className="content-header-left mb-1">
            <SearchBox onSearch={(text) => onSearch(text.trim())} />
          </div>

          <div className="content-header-right mb-1">
            {/* ap5-a4: the custom range only shows its dates once Custom is chosen. */}
            {dueFilter === CUSTOM && (
              <>
                <DateField
                  fieldClassName="w-150"
                  placeholder="Due from"
                  value={customFrom}
                  onChange={(value) => {
                    setCustomFrom(value);
                    onCustomDateChange(value, customTo);
                  }}
                />
                <DateField
                  fieldClassName="w-150"
                  placeholder="Due to"
                  min={customFrom ?? undefined}
                  value={customTo}
                  onChange={(value) => {
                    setCustomTo(value);
                    onCustomDateChange(customFrom, value);
                  }}
                />
              </>
            )}
            <Select
              className="w-150"
              openOnFocus
              panelClass="myPanelClass"
              items={dueOptions}
              bindLabel="name"
              bindValue="id"
              clearable={false}
              searchable={false}
              value={dueFilter}
              onChange={(value) => onDueChange(value as number | null)}
            />
            <Select
              className="w-150"
              openOnFocus
              panelClass="myPanelClass"
              items={statusOptions}
              bindLabel="name"
              bindValue="id"
              clearable={false}
              searchable={false}
              value={statusFilter}
              onChange={(value) => onStatusChange(value as number | null)}
            />
            <Select
              className="w-150"
              openOnFocus
              panelClass="myPanelClass"
              items={clientTypeOptions}
              bindLabel="name"
              bindValue="id"
              clearable={false}
              searchable={false}
              value={clientTypeFilter}
              onChange={(value) => onClientTypeChange(value as number | null)}
            />
            <ExportMenu onExport={onExport} />
          </div>
        </div>

        {activeFilters.length > 0 && (
          <div className="accounts-filter-chip mb-1">
            <span>
              Filtered by: <strong>{activeFilters.join(', ')}</strong>
            </span>
            <button type="button" className="accounts-filter-clear" onClick={clearUrlFilter}>
              Clear
            </button>
          </div>
        )}

        {selectedIds.size > 0 && (
          <div className="d-flex align-items-center gap-15 mb-1">
            <span className="fw-bold">{selectedIds.size} selected</span>
            <Button className="action-button danger-button" onClick={deleteSelected}>
              <Icon name="delete_outline" outlined />
              <span>Delete selected</span>
            </Button>
            <Button className="action-button secondary-button" onClick={() => onExport(ExportFormatCode.Excel)}>
              <Icon name="border_all" className="icon-color-xls" />
              <span>Export to Excel</span>
            </Button>
          </div>
        )}

        <div className={totalRecord > 0 ? 'mat-table-wrapper mat-table-wrapper-pagination' : 'mat-table-wrapper'}>
          <DataTable
            stickyHeader
            columns={columns}
            rows={dataSource}
            rowKey={(row) => row.universalId}
            sort={sort}
            onSort={onSort}
          />
        </div>

        {dataSource.length === 0 && <div className="text-align-center p-1">No clients match these filters.</div>}

        {/* prod-p2: the list is paged on the server, so hundreds of clients load one page at a time. */}
        {totalRecord > 0 && (
          <Paginator
            length={totalRecord}
            pageSize={pageSize}
            pageIndex={pageIndex}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            showFirstLastButtons
            onPage={onPage}
          />
        )}
      </div>
    </div>
  );
}
